import React from "react";
import { Col, Row, Card, CardHeader, CardBody, Collapse } from "reactstrap";
import TableWrapper from "@/utils/TableWrapper";
import type { TableWrapperProps } from "@/utils/TableWrapper";

export type DisplayPendingPagesViewProps = {
  alertNode: React.ReactNode;
  nonPublishedPages: unknown[];
  pendingColumns: TableWrapperProps<unknown>["columns"];
  publishedPages: unknown[];
  publishedColumns: TableWrapperProps<unknown>["columns"];
  collapseOpen: boolean;
  toggleCollapse: () => void;
};

/**
 * SCRUM-1151: both lists render through TableWrapper, so this view no longer
 * carries a search box, a pager or a hand-rolled `<Table>`. The container keeps
 * the updatedAt sort and hands the rows in as `data`, so most-recent-first stays
 * the default order while the headers gain click-to-sort.
 *
 * Known consequences, recorded rather than discovered. The queue's own
 * "Search .." box matched the page title only; TableWrapper's built-in box
 * searches every column that exposes a value, so it now matches "Updated By"
 * too, and the published history — which had no search at all — gains one.
 * The accessible name on both boxes came with SCRUM-1149. The duplicate
 * `id="searchInput"` that having two tables on one screen produced is fixed by
 * SCRUM-1159: TableWrapper takes a `searchId`, and the two boxes here pass
 * their own.
 */
const DisplayPendingPagesView = ({
  // alert
  alertNode,

  // pending table
  nonPublishedPages,
  pendingColumns,

  // published table
  publishedPages,
  publishedColumns,

  // accordion
  collapseOpen,
  toggleCollapse,
}: DisplayPendingPagesViewProps) => {
  return (
    <>
      {/* ALERT */}
      {alertNode}

      {/* Pending Pages */}
      <Card>
        <CardHeader>
          <Row>
            <Col xs="9">
              <h5 className="h3 mb-0">Publish Pages</h5>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Col>
            <TableWrapper
              data={nonPublishedPages}
              columns={pendingColumns}
              searchId="pendingPagesSearchInput"
            />
          </Col>
        </CardBody>
      </Card>

      {/* Published Pages History */}
      <Card>
        <CardHeader>
          <Row>
            <Col xs="9">
              <h5 className="h3 mb-0">
                <a onClick={toggleCollapse} role="button" style={{ cursor: "pointer" }}>
                  Published Pages History
                </a>
              </h5>
            </Col>
          </Row>
        </CardHeader>

        <Collapse isOpen={collapseOpen}>
          <CardBody>
            <Col>
              <TableWrapper
                data={publishedPages}
                columns={publishedColumns}
                searchId="publishedPagesSearchInput"
              />
            </Col>
          </CardBody>
        </Collapse>
      </Card>
    </>
  );
};

export default DisplayPendingPagesView;
